// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  Table,
  Button,
  Icon,
  Label,
  Message,
  Segment,
  Input,
  Dropdown,
  Pagination,
  Modal,
} from 'semantic-ui-react';
import { supportStaffApi } from '../api/supportStaffApi';
import { brokerageApi, Brokerage } from '../api/brokerageApi';
import type { SupportStaff, PageResponse } from '../types/supportStaff';
import InviteSupportStaffModal from '../components/InviteSupportStaffModal';
import { formatRelativeTime } from '@loa/theme';

function SupportStaffListPage() {
  const navigate = useNavigate();
  const [supportStaff, setSupportStaff] = useState<SupportStaff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [brokerageFilter, setBrokerageFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [brokerages, setBrokerages] = useState<Brokerage[]>([]);

  // Modal states
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [staffToDeactivate, setStaffToDeactivate] = useState<SupportStaff | null>(null);
  const [deactivating, setDeactivating] = useState(false);

  const statusOptions = [
    { key: 'all', value: 'all', text: 'All Statuses' },
    { key: 'active', value: 'active', text: 'Active' },
    { key: 'inactive', value: 'inactive', text: 'Inactive' },
    { key: 'invited', value: 'invited', text: 'Invited' },
  ];

  const loadBrokerages = useCallback(async () => {
    try {
      const response = await brokerageApi.getAll({ page: 0, size: 100 });
      setBrokerages(response.content);
    } catch (err) {
      console.error('Failed to load brokerages:', err);
    }
  }, []);

  const loadSupportStaff = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response: PageResponse<SupportStaff> = await supportStaffApi.getAll(
        currentPage,
        pageSize,
        'createdAt',
        'DESC',
        statusFilter === 'all' ? undefined : statusFilter,
        brokerageFilter === 'all' ? undefined : brokerageFilter,
        searchQuery || undefined
      );
      setSupportStaff(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load support staff');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, statusFilter, brokerageFilter, searchQuery]);

  useEffect(() => {
    loadBrokerages();
  }, [loadBrokerages]);

  useEffect(() => {
    loadSupportStaff();
  }, [loadSupportStaff]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [statusFilter, brokerageFilter, pageSize]);

  const handleSearch = () => {
    setCurrentPage(0);
    loadSupportStaff();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleDeactivateClick = (staff: SupportStaff) => {
    setStaffToDeactivate(staff);
    setDeactivateModalOpen(true);
  };

  const handleDeactivateConfirm = async () => {
    if (!staffToDeactivate?.id) return;

    setDeactivating(true);
    try {
      await supportStaffApi.deactivate(staffToDeactivate.id);
      setDeactivateModalOpen(false);
      setStaffToDeactivate(null);
      await loadSupportStaff();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate support staff');
    } finally {
      setDeactivating(false);
    }
  };

  const handleDeactivateCancel = () => {
    setDeactivateModalOpen(false);
    setStaffToDeactivate(null);
  };

  const handleInviteSuccess = () => {
    setInviteModalOpen(false);
    loadSupportStaff();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'inactive':
        return 'grey';
      case 'invited':
        return 'blue';
      default:
        return 'grey';
    }
  };

  const brokerageOptions = [
    { key: 'all', value: 'all', text: 'All Brokerages' },
    ...brokerages.map((b) => ({ key: b.id, value: b.id, text: b.name })),
  ];

  return (
    <Container style={{ marginTop: '2rem' }}>
      <Header as="h1">
        <Icon name="users" />
        <Header.Content>
          Support Staff
          <Header.Subheader>Manage support staff across all brokerages</Header.Subheader>
        </Header.Content>
      </Header>

      {error && (
        <Message negative onDismiss={() => setError(null)}>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <Segment>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <Input
            icon="search"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            style={{ flex: 1, minWidth: '200px' }}
          />
          <Button primary onClick={handleSearch}>
            Search
          </Button>
          <Dropdown
            selection
            options={statusOptions}
            value={statusFilter}
            onChange={(_, data) => setStatusFilter(data.value as string)}
            placeholder="Filter by status"
            style={{ minWidth: '150px' }}
          />
          <Dropdown
            selection
            options={brokerageOptions}
            value={brokerageFilter}
            onChange={(_, data) => setBrokerageFilter(data.value as string)}
            placeholder="Filter by brokerage"
            style={{ minWidth: '180px' }}
          />
          <Button positive onClick={() => setInviteModalOpen(true)}>
            <Icon name="plus" />
            Invite Support
          </Button>
        </div>

        {loading && <p>Loading support staff...</p>}
        {!loading && supportStaff.length === 0 && (
          <Message info>
            <Message.Header>No Support Staff Found</Message.Header>
            <p>Invite your first support staff member using the button above.</p>
          </Message>
        )}
        {!loading && supportStaff.length > 0 && (
          <>
            <Table celled striped>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Name</Table.HeaderCell>
                  <Table.HeaderCell>Email</Table.HeaderCell>
                  <Table.HeaderCell>Brokerage</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Assigned Advisors</Table.HeaderCell>
                  <Table.HeaderCell>Last Login</Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {supportStaff.map((staff) => (
                  <Table.Row key={staff.id}>
                    <Table.Cell>
                      {staff.firstName} {staff.lastName}
                    </Table.Cell>
                    <Table.Cell>{staff.email}</Table.Cell>
                    <Table.Cell>{staff.brokerageName || 'Unknown'}</Table.Cell>
                    <Table.Cell>
                      <Label color={getStatusColor(staff.status)} size="small">
                        {staff.status.toUpperCase()}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <button
                        type="button"
                        onClick={() => navigate(`/support-staff/${staff.id}`)}
                        style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0, color: '#4183c4', textDecoration: 'underline', font: 'inherit' }}
                      >
                        {staff.assignedAdvisorCount ?? 0}
                      </button>
                    </Table.Cell>
                    <Table.Cell>{formatRelativeTime(staff.lastLoginAt)}</Table.Cell>
                    <Table.Cell>
                      <Dropdown
                        icon="ellipsis vertical"
                        floating
                        button
                        className="icon"
                        direction="left"
                      >
                        <Dropdown.Menu>
                          <Dropdown.Item
                            icon="eye"
                            text="View Details"
                            onClick={() => navigate(`/support-staff/${staff.id}`)}
                          />
                          <Dropdown.Item
                            icon="edit"
                            text="Edit"
                            onClick={() => navigate(`/support-staff/${staff.id}/edit`)}
                          />
                          <Dropdown.Item
                            icon="exchange"
                            text="Manage Assignments"
                            onClick={() => navigate(`/support-staff/${staff.id}?tab=assignments`)}
                          />
                          <Dropdown.Divider />
                          {staff.status === 'active' && (
                            <Dropdown.Item
                              icon="user times"
                              text="Deactivate"
                              onClick={() => handleDeactivateClick(staff)}
                              style={{ color: 'red' }}
                            />
                          )}
                          {staff.status === 'inactive' && (
                            <Dropdown.Item
                              icon="user plus"
                              text="Reactivate"
                              onClick={async () => {
                                try {
                                  await supportStaffApi.reactivate(staff.id);
                                  await loadSupportStaff();
                                } catch (err) {
                                  setError(
                                    err instanceof Error
                                      ? err.message
                                      : 'Failed to reactivate'
                                  );
                                }
                              }}
                              style={{ color: 'green' }}
                            />
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>

            <div
              style={{
                marginTop: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '10px',
              }}
            >
              <div style={{ color: '#666' }}>
                Showing {currentPage * pageSize + 1}-
                {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements}{' '}
                support staff member{totalElements === 1 ? '' : 's'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span>Items per page:</span>
                <Dropdown
                  compact
                  selection
                  value={pageSize}
                  options={[
                    { key: 10, text: '10', value: 10 },
                    { key: 20, text: '20', value: 20 },
                    { key: 50, text: '50', value: 50 },
                    { key: 100, text: '100', value: 100 },
                  ]}
                  onChange={(_e, data) => {
                    setPageSize(data.value as number);
                    setCurrentPage(0);
                  }}
                />
                {totalPages > 1 && (
                  <Pagination
                    activePage={currentPage + 1}
                    totalPages={totalPages}
                    onPageChange={(_e, data) => setCurrentPage((data.activePage as number) - 1)}
                    siblingRange={1}
                    boundaryRange={0}
                    ellipsisItem={null}
                    firstItem={null}
                    lastItem={null}
                    prevItem={{ content: <Icon name="chevron left" />, icon: true }}
                    nextItem={{ content: <Icon name="chevron right" />, icon: true }}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </Segment>

      {/* Invite Support Staff Modal */}
      <InviteSupportStaffModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onSuccess={handleInviteSuccess}
        brokerages={brokerages}
      />

      {/* Deactivate Confirmation Modal */}
      <Modal open={deactivateModalOpen} onClose={handleDeactivateCancel} size="small">
        <Header icon="user times" content="Deactivate Support Staff" />
        <Modal.Content>
          <p>
            Are you sure you want to deactivate support staff member{' '}
            <strong>
              {staffToDeactivate?.firstName} {staffToDeactivate?.lastName}
            </strong>{' '}
            ({staffToDeactivate?.email})?
          </p>
          <p>
            This will revoke their access to the platform. They can be reactivated later if
            needed.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleDeactivateCancel} disabled={deactivating}>
            <Icon name="remove" /> Cancel
          </Button>
          <Button negative onClick={handleDeactivateConfirm} loading={deactivating}>
            <Icon name="checkmark" /> Deactivate
          </Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
}

export default SupportStaffListPage;
